import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { commonIcon } from "../../asset";
import "./StatusPage.css";

const API_BASE = "";

const COMPONENT_STATUS_LABELS = {
  operational: "Operational",
  degraded: "Degraded Performance",
  partial_outage: "Partial Outage",
  major_outage: "Major Outage",
};

const INCIDENT_STATUS_LABELS = {
  investigating: "Investigating",
  identified: "Identified",
  monitoring: "Monitoring",
  resolved: "Resolved",
};

const OVERALL_MESSAGES = {
  operational: "All Systems Operational",
  degraded: "Degraded Performance",
  partial_outage: "Partial System Outage",
  major_outage: "Major System Outage",
};

const OVERALL_ICONS = {
  operational: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="13" stroke="#fff" strokeWidth="2" />
      <path d="M9 14.5l3.5 3.5 6.5-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  degraded: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="13" stroke="#fff" strokeWidth="2" />
      <rect x="13" y="8" width="2" height="8" rx="1" fill="#fff" />
      <circle cx="14" cy="20" r="1.5" fill="#fff" />
    </svg>
  ),
  partial_outage: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="13" stroke="#fff" strokeWidth="2" />
      <rect x="13" y="8" width="2" height="8" rx="1" fill="#fff" />
      <circle cx="14" cy="20" r="1.5" fill="#fff" />
    </svg>
  ),
  major_outage: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="13" stroke="#fff" strokeWidth="2" />
      <path d="M10 10l8 8M18 10l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

function getOverallStatus(components) {
  if (!components || components.length === 0) return "operational";
  if (components.some((c) => c.status === "major_outage")) return "major_outage";
  if (components.some((c) => c.status === "partial_outage")) return "partial_outage";
  if (components.some((c) => c.status === "degraded")) return "degraded";
  return "operational";
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function formatDateTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isToday(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  return dateStr === today;
}

function timeAgo(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── 90-day Uptime Bar ───
function UptimeBar({ days }) {
  if (!days || days.length === 0) return null;

  // days[0] = today, days[89] = 90 days ago
  // Display left-to-right: oldest first → newest
  const reversed = [...days].reverse();

  return (
    <div className="ds-uptime-bar" title="90-day uptime">
      {reversed.map((status, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (days.length - 1 - i));
        const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        const label =
          status === "operational" ? "No issues" :
          status === "partial_outage" ? "Partial outage" : "Major outage";

        return (
          <div
            key={i}
            className={`ds-uptime-bar-segment ds-uptime-${status}`}
            title={`${dateStr}: ${label}`}
          />
        );
      })}
    </div>
  );
}

// ─── Subscribe Dropdown ───
function SubscribeDropdown({ onClose, isSubscribed }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState(isSubscribed ? "subscribed" : "idle");
  const [errorMsg, setErrorMsg] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API_BASE}/api/status/subscribe`, { email });
      if (res.data.message === "Already subscribed") {
        setState("exists");
      } else {
        setState("success");
      }
    } catch {
      setState("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="ds-sub-dropdown" ref={ref} role="dialog" aria-label="Subscribe to updates">
      <div className="ds-sub-dropdown-header">
        <span>Subscribe to Updates</span>
        <button className="ds-sub-dropdown-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      {state === "subscribed" ? (
        <div className="ds-sub-dropdown-result ds-sub-dropdown-subscribed">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 10l2 2 4-4" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="10" cy="10" r="9" stroke="#15803d" strokeWidth="1.5" />
          </svg>
          <p>You're already subscribed to status updates via your account email.</p>
        </div>
      ) : state === "success" ? (
        <div className="ds-sub-dropdown-result">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 10l2 2 4-4" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="10" cy="10" r="9" stroke="#15803d" strokeWidth="1.5" />
          </svg>
          <p>Check your inbox — confirmation email sent to <strong>{email}</strong>.</p>
        </div>
      ) : state === "exists" ? (
        <div className="ds-sub-dropdown-result">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 10l2 2 4-4" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="10" cy="10" r="9" stroke="#15803d" strokeWidth="1.5" />
          </svg>
          <p>You are already subscribed to status updates.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="ds-sub-dropdown-form">
          <div className="ds-sub-directions">
            Get email notifications whenever Gospel Truth AI <strong>creates</strong>,{" "}
            <strong>updates</strong> or <strong>resolves</strong> an incident.
          </div>
          <label className="ds-sub-label" htmlFor="ds-sub-email">Email address</label>
          <input
            id="ds-sub-email"
            type="email"
            className="ds-sub-input"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={state === "loading"}
            autoComplete="email"
          />
          {state === "error" && <p className="ds-sub-error">{errorMsg}</p>}
          <button type="submit" className="ds-sub-submit" disabled={state === "loading"}>
            {state === "loading" ? "Subscribing…" : "Subscribe via Email"}
          </button>
        </form>
      )}
    </div>
  );
}

// ─── Confirm / Unsubscribe Banner ───
function ConfirmBanner({ onDismiss }) {
  const params = new URLSearchParams(window.location.search);
  const confirm = params.get("confirm");
  const unsubscribe = params.get("unsubscribe");

  if (!confirm && !unsubscribe) return null;

  let type = "success";
  let msg = "";
  if (confirm === "success") msg = "You have been successfully subscribed. Thank you!";
  else if (confirm === "invalid") { type = "error"; msg = "This confirmation link is invalid or has already been used."; }
  else if (unsubscribe === "success") { type = "info"; msg = "You have been unsubscribed from status updates."; }
  else if (unsubscribe === "invalid") { type = "error"; msg = "This unsubscribe link is invalid."; }
  else return null;

  return (
    <div className={`ds-banner ds-banner-${type}`}>
      <span>{msg}</span>
      <button className="ds-banner-close" onClick={onDismiss}>×</button>
    </div>
  );
}

// ─── Component Row ───
function ComponentRow({ component }) {
  const statusClass = `ds-comp-status-${component.status}`;
  const uptimeData = component.uptime || { days: Array(90).fill("operational"), uptimePct: 100 };

  return (
    <div className="ds-comp-row">
      <div className="ds-comp-row-header">
        <span className="ds-comp-name">{component.name}</span>
        <span className={`ds-comp-status ${statusClass}`}>
          {COMPONENT_STATUS_LABELS[component.status] || component.status}
        </span>
      </div>
      <div className="ds-comp-uptime-wrap">
        <UptimeBar days={uptimeData.days} />
        <div className="ds-comp-uptime-labels">
          <span className="ds-comp-uptime-label-left">90 days ago</span>
          <span className="ds-comp-uptime-pct">{uptimeData.uptimePct}% uptime</span>
          <span className="ds-comp-uptime-label-right">Today</span>
        </div>
      </div>
    </div>
  );
}

// ─── Active Incident Card ───
function ActiveIncidentCard({ incident }) {
  const latestUpdate = incident.updates && incident.updates.length > 0
    ? incident.updates[incident.updates.length - 1]
    : null;

  return (
    <div className={`ds-active-incident ds-impact-${incident.impact || "minor"}`}>
      <div className="ds-active-incident-header">
        <span className={`ds-inc-badge ds-inc-${incident.status}`}>
          {INCIDENT_STATUS_LABELS[incident.status] || incident.status}
        </span>
        <span className="ds-active-incident-title">{incident.title}</span>
      </div>
      {incident.components && incident.components.length > 0 && (
        <p className="ds-active-incident-components">
          Affected: {incident.components.map((c) => c.name || c).join(", ")}
        </p>
      )}
      {latestUpdate && (
        <div className="ds-active-incident-latest">
          <p className="ds-active-incident-body">{latestUpdate.body}</p>
          <span className="ds-active-incident-time">
            {formatDateTime(latestUpdate.createdAt)} ({timeAgo(latestUpdate.createdAt)})
          </span>
        </div>
      )}
      {/* Timeline of all updates */}
      {incident.updates && incident.updates.length > 1 && (
        <div className="ds-active-incident-timeline">
          {[...incident.updates].reverse().slice(1).map((u, i) => (
            <div key={i} className="ds-timeline-entry">
              <span className={`ds-inc-badge-sm ds-inc-${u.status}`}>
                {INCIDENT_STATUS_LABELS[u.status] || u.status}
              </span>
              <span className="ds-timeline-body">{u.body}</span>
              <span className="ds-timeline-time">{formatDateTime(u.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
//  MAIN STATUS PAGE
// ═══════════════════════════════════════
export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [expandedIncident, setExpandedIncident] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [pastIncidentsLimit, setPastIncidentsLimit] = useState(7);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/status`);
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check subscription status (relaxed auth — works for logged-in users)
  useEffect(() => {
    axios.get(`${API_BASE}/api/status/my-subscription`, { withCredentials: true })
      .then((res) => {
        if (res.data?.subscribed) setIsSubscribed(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const overall = data ? getOverallStatus(data.components) : "operational";
  const hasActiveIncidents = data?.activeIncidents?.length > 0;

  // Past incidents with "show more"
  const pastDays = useMemo(() => {
    if (!data?.pastIncidentsByDay) return [];
    return data.pastIncidentsByDay.slice(0, pastIncidentsLimit);
  }, [data, pastIncidentsLimit]);

  const hasMorePast = data?.pastIncidentsByDay?.length > pastIncidentsLimit;

  return (
    <div className="ds-page">
      {/* ── Masthead ── */}
      <header className="ds-masthead">
        <div className="ds-masthead-inner">
          <a href="https://gospeltruth.ai" className="ds-logo-link">
            <img src={commonIcon.fullLogo2} alt="Gospel Truth AI" className="ds-logo-img" />
          </a>

          <div className="ds-subscribe-wrap">
            {isSubscribed && (
              <span className="ds-subscribed-badge">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M7 10l2 2 4-4" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="10" cy="10" r="9" stroke="#15803d" strokeWidth="1.5" />
                </svg>
                Subscribed
              </span>
            )}
            <button
              className="ds-subscribe-btn"
              onClick={() => setShowDropdown((v) => !v)}
              aria-expanded={showDropdown}
              aria-haspopup="dialog"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              <span className="ds-subscribe-text-full">Subscribe to Updates</span>
              <span className="ds-subscribe-text-short">Subscribe</span>
            </button>

            {showDropdown && (
              <SubscribeDropdown
                onClose={() => setShowDropdown(false)}
                isSubscribed={isSubscribed}
              />
            )}
          </div>
        </div>
      </header>

      {/* ── Overall Status Banner ── */}
      <div className={`ds-status-banner ds-status-${overall}`}>
        <div className="ds-status-banner-inner">
          {bannerVisible && <ConfirmBanner onDismiss={() => setBannerVisible(false)} />}
          <div className="ds-status-banner-content">
            {OVERALL_ICONS[overall]}
            <h1 className="ds-status-text">{OVERALL_MESSAGES[overall]}</h1>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <main className="ds-content">
        {loading ? (
          <div className="ds-loading">
            <div className="ds-spinner" />
            <span>Loading status…</span>
          </div>
        ) : (
          <>
            {/* ── Active Incidents ── */}
            {hasActiveIncidents && (
              <section className="ds-section ds-active-incidents-section">
                <h2 className="ds-section-title">Active Incidents</h2>
                {data.activeIncidents.map((inc) => (
                  <ActiveIncidentCard key={inc._id} incident={inc} />
                ))}
              </section>
            )}

            {/* ── Component Status ── */}
            {data?.components?.length > 0 && (
              <section className="ds-section ds-components-section">
                <h2 className="ds-section-title">System Status</h2>
                <div className="ds-comp-list">
                  {data.components.map((comp) => (
                    <ComponentRow key={comp._id} component={comp} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Uptime Legend ── */}
            <div className="ds-legend">
              <div className="ds-legend-item">
                <span className="ds-legend-color ds-legend-operational" />
                <span>No issues</span>
              </div>
              <div className="ds-legend-item">
                <span className="ds-legend-color ds-legend-partial" />
                <span>Partial outage</span>
              </div>
              <div className="ds-legend-item">
                <span className="ds-legend-color ds-legend-major" />
                <span>Major outage</span>
              </div>
            </div>

            {/* ── Past Incidents ── */}
            <section className="ds-section ds-past-incidents-section">
              <h2 className="ds-section-title">Past Incidents</h2>

              {pastDays.map((dayGroup) => (
                <div key={dayGroup.date} className="ds-day-group">
                  <h3 className="ds-day-title">{formatDate(dayGroup.date)}</h3>
                  {dayGroup.incidents.length === 0 ? (
                    <p className="ds-day-no-incidents">
                      No incidents reported{isToday(dayGroup.date) ? " today" : ""}.
                    </p>
                  ) : (
                    dayGroup.incidents.map((inc) => (
                      <div key={inc._id} className="ds-past-incident">
                        <div
                          className="ds-past-incident-header"
                          onClick={() =>
                            setExpandedIncident(expandedIncident === inc._id ? null : inc._id)
                          }
                        >
                          <span className="ds-inc-badge ds-inc-resolved">Resolved</span>
                          <span className="ds-past-incident-title">{inc.title}</span>
                          <span className="ds-expand-arrow">
                            {expandedIncident === inc._id ? "▲" : "▼"}
                          </span>
                        </div>
                        {expandedIncident === inc._id && (
                          <div className="ds-past-incident-detail">
                            {inc.components?.length > 0 && (
                              <p className="ds-past-incident-components">
                                Affected: {inc.components.map((c) => c.name).join(", ")}
                              </p>
                            )}
                            <div className="ds-past-incident-updates">
                              {[...(inc.updates || [])].reverse().map((u, i) => (
                                <div key={i} className="ds-timeline-entry">
                                  <span className={`ds-inc-badge-sm ds-inc-${u.status}`}>
                                    {INCIDENT_STATUS_LABELS[u.status] || u.status}
                                  </span>
                                  <span className="ds-timeline-body">{u.body}</span>
                                  <span className="ds-timeline-time">{formatDateTime(u.createdAt)}</span>
                                </div>
                              ))}
                            </div>
                            <p className="ds-resolved-at">
                              Resolved: {inc.resolvedAt ? formatDateTime(inc.resolvedAt) : "—"}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              ))}

              {pastDays.length === 0 && (
                <p className="ds-empty">No incident history available.</p>
              )}

              {hasMorePast && (
                <button
                  className="ds-show-more"
                  onClick={() => setPastIncidentsLimit((l) => l + 14)}
                >
                  Show older incidents
                </button>
              )}
            </section>
          </>
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="ds-footer">
        <p>
          © {new Date().getFullYear()} Gospel Truth AI ·{" "}
          <a href="https://gospeltruth.ai" className="ds-footer-link">gospeltruth.ai</a>
        </p>
      </footer>
    </div>
  );
}
